/**
 * Utility functions for tournament ranking calculations
 */

/**
 * Bench points for one Americano round when a player does not play (half of target, rounded up).
 * @param {number} numberOfPoints - Tournament "points to win" target
 * @returns {number}
 */
export const benchPointsForSittingOut = (numberOfPoints) => {
    const n = Number(numberOfPoints);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.ceil(n / 2);
};

/**
 * Calculate player statistics and ranking for a tournament
 * @param {Object} tournament - The tournament object
 * @returns {Array} Array of player stats with ranking information
 */
export const calculatePlayerStats = (tournament) => {
    if (!tournament) return [];

    const playerStats = new Map();

    const ensurePlayer = (participant, displayName) => {
        if (!participant?.id || playerStats.has(participant.id)) return;
        playerStats.set(participant.id, {
            id: participant.id,
            name: displayName || participant.name,
            points: 0,
            wins: 0,
            losses: 0,
            ties: 0
        });
    };

    tournament.participants?.forEach((tp) => {
        if (tp?.participant) {
            ensurePlayer(tp.participant, tp.displayName || tp.participant.name);
        }
    });

    // Initialize remaining players seen only in matches
    tournament.rounds?.forEach(round => {
        round.matches?.forEach(match => {
            [match.team1Participant1, match.team1Participant2,
             match.team2Participant1, match.team2Participant2].forEach(participant => {
                ensurePlayer(participant, participant?.name);
            });
        });
    });

    // Calculate points and match results from matches
    tournament.rounds?.forEach(round => {
        round.matches?.forEach(match => {
            if (match.team1Result !== null && match.team2Result !== null) {
                const team1Score = match.team1Result || 0;
                const team2Score = match.team2Result || 0;
                const team1Won = team1Score > team2Score;
                const isTie = team1Score === team2Score;

                // Process team 1 players
                [match.team1Participant1, match.team1Participant2].forEach(participant => {
                    if (participant) {
                        ensurePlayer(participant, participant.name);
                        const player = playerStats.get(participant.id);
                        if (player) {
                            player.points += team1Score;
                            if (isTie) player.ties++;
                            else if (team1Won) player.wins++;
                            else player.losses++;
                        }
                    }
                });

                // Process team 2 players
                [match.team2Participant1, match.team2Participant2].forEach(participant => {
                    if (participant) {
                        ensurePlayer(participant, participant.name);
                        const player = playerStats.get(participant.id);
                        if (player) {
                            player.points += team2Score;
                            if (isTie) player.ties++;
                            else if (!team1Won) player.wins++;
                            else player.losses++;
                        }
                    }
                });
            }
        });
    });

    if (tournament.type === 'americano') {
        const benchPts = benchPointsForSittingOut(tournament.numberOfPoints);
        if (benchPts > 0) {
            tournament.rounds?.forEach((round) => {
                if (!round.matches?.length) return;
                const allScored = round.matches.every(
                    (m) => m.team1Result !== null && m.team2Result !== null
                );
                if (!allScored) return;
                const playingIds = new Set();
                round.matches.forEach((m) => {
                    [m.team1Participant1, m.team1Participant2, m.team2Participant1, m.team2Participant2].forEach((p) => {
                        if (p?.id) playingIds.add(p.id);
                    });
                });
                tournament.participants?.forEach((tp) => {
                    const p = tp.participant;
                    if (!p?.id || playingIds.has(p.id)) return;
                    ensurePlayer(p, tp.displayName || p.name);
                    const row = playerStats.get(p.id);
                    if (row) row.points += benchPts;
                });
            });
        }
    }

    // Sort by ranking criteria:
    // 1. Number of points (descending)
    // 2. If equal -> number of wins (descending)
    // 3. If equal -> number of ties (descending)
    // 4. If equal -> both players should be on the same place
    const sortedStats = Array.from(playerStats.values())
        .sort((a, b) => {
            // First criteria: points (descending)
            if (b.points !== a.points) {
                return b.points - a.points;
            }
            
            // Second criteria: wins (descending)
            if (b.wins !== a.wins) {
                return b.wins - a.wins;
            }
            
            // Third criteria: ties (descending)
            if (b.ties !== a.ties) {
                return b.ties - a.ties;
            }
            
            // If all criteria are equal, maintain current order (same place)
            return 0;
        });

    // Add ranking positions (handling ties)
    let currentRank = 1;
    let currentPosition = 1;
    
    for (let i = 0; i < sortedStats.length; i++) {
        const currentPlayer = sortedStats[i];
        const nextPlayer = sortedStats[i + 1];
        
        // Set current rank
        currentPlayer.rank = currentRank;
        
        // Check if next player has the same ranking criteria
        if (nextPlayer && 
            nextPlayer.points === currentPlayer.points && 
            nextPlayer.wins === currentPlayer.wins && 
            nextPlayer.ties === currentPlayer.ties) {
            // Same rank for next player - keep current rank
        } else {
            // Increment rank for next player
            currentRank = currentPosition + 1;
        }
        
        currentPosition++;
    }

    return sortedStats;
};

/**
 * Get top N players from tournament ranking
 * @param {Object} tournament - The tournament object
 * @param {number} count - Number of top players to return (default: 3)
 * @returns {Array} Array of top N players with ranking information
 */
export const getTopPlayers = (tournament, count = 3) => {
    const allPlayers = calculatePlayerStats(tournament);
    return allPlayers.slice(0, count);
};

/**
 * Get players with rank N or better
 * @param {Object} tournament - The tournament object
 * @param {number} maxRank - Maximum rank to include (default: 3)
 * @returns {Array} Array of players with rank <= maxRank
 */
export const getPlayersByRank = (tournament, maxRank = 3) => {
    const allPlayers = calculatePlayerStats(tournament);
    return allPlayers.filter(player => player.rank <= maxRank);
};

/**
 * Get players with ranking and match details
 * @param {Object} tournament - The tournament object
 * @returns {Array} Array of players with ranking and match details
 */
export const getPlayersWithMatchDetails = (tournament) => {
    if (!tournament) return [];

    const allPlayers = calculatePlayerStats(tournament);
    const rosterIds = new Set(
        (tournament.participants || [])
            .map((tp) => tp.participant?.id)
            .filter(Boolean)
    );

    const playersWithMatches = allPlayers.map((player) => {
        const playerWithMatches = { ...player, matches: [] };

        tournament.rounds?.forEach((round) => {
            if (!round.matches?.length) return;

            const playingIds = new Set();
            round.matches.forEach((m) => {
                [m.team1Participant1, m.team1Participant2, m.team2Participant1, m.team2Participant2].forEach(
                    (p) => {
                        if (p?.id) playingIds.add(p.id);
                    }
                );
            });

            const allScored = round.matches.every(
                (m) => m.team1Result !== null && m.team2Result !== null
            );

            round.matches.forEach((match) => {
                if (match.team1Result === null || match.team2Result === null) return;

                const team1Score = match.team1Result || 0;
                const team2Score = match.team2Result || 0;
                const team1Won = team1Score > team2Score;
                const isTie = team1Score === team2Score;
                const roundOrder = round.order;
                const roundDisplayNumber = roundOrder + 1;

                if (match.team1Participant1?.id === player.id || match.team1Participant2?.id === player.id) {
                    playerWithMatches.matches.push({
                        match,
                        roundOrder,
                        roundDisplayNumber,
                        isBench: false,
                        teamScore: team1Score,
                        opponentScore: team2Score,
                        won: team1Won,
                        isTie,
                        teammates: [match.team1Participant1, match.team1Participant2]
                            .filter((p) => p && p.id !== player.id)
                            .map((p) => p.name),
                        opponents: [match.team2Participant1, match.team2Participant2]
                            .filter(Boolean)
                            .map((p) => p.name),
                    });
                }

                if (match.team2Participant1?.id === player.id || match.team2Participant2?.id === player.id) {
                    playerWithMatches.matches.push({
                        match,
                        roundOrder,
                        roundDisplayNumber,
                        isBench: false,
                        teamScore: team2Score,
                        opponentScore: team1Score,
                        won: !team1Won,
                        isTie,
                        teammates: [match.team2Participant1, match.team2Participant2]
                            .filter((p) => p && p.id !== player.id)
                            .map((p) => p.name),
                        opponents: [match.team1Participant1, match.team1Participant2]
                            .filter(Boolean)
                            .map((p) => p.name),
                    });
                }
            });

            if (
                tournament.type === 'americano' &&
                rosterIds.has(player.id) &&
                allScored &&
                !playingIds.has(player.id)
            ) {
                const benchPts = benchPointsForSittingOut(tournament.numberOfPoints);
                playerWithMatches.matches.push({
                    isBench: true,
                    roundOrder: round.order,
                    roundDisplayNumber: round.order + 1,
                    benchPoints: benchPts,
                });
            }
        });

        playerWithMatches.matches.sort((a, b) => {
            const ra = a.roundOrder ?? 0;
            const rb = b.roundOrder ?? 0;
            if (ra !== rb) return ra - rb;
            if (a.isBench && !b.isBench) return 1;
            if (!a.isBench && b.isBench) return -1;
            return 0;
        });

        return playerWithMatches;
    });

    return playersWithMatches;
};
